/**
 * Free Shipping Bar Handler
 * Alpine.js store and component for managing free shipping progress bar state
 * Handles progress preservation during cart updates to prevent animation resets
 */
if (!window.Eurus.loadedScript.has('freeshipping-bar-handler.js')) {
  window.Eurus.loadedScript.add('freeshipping-bar-handler.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      // Create Alpine store to preserve progress state across cart sections re-renders
      // This prevents the progress bar from resetting when cart is updated
      Alpine.store('freeshippingBar', {
        instances: {},
        
        // Get or initialize state for a specific container instance
        getState(containerId) {
          if (!this.instances[containerId]) {
            this.instances[containerId] = {
              progress: 0, // Current progress percentage
              active: false,
              initialized: false,
            };
          }
          return this.instances[containerId];
        },
        
        saveProgress(container) {
          if (!container || !container.id) return 0;
          
          const containerId = container.id;
          const progressContainer = container.querySelector('[x-data*="freeshippingBar"]');
          
          if (progressContainer && window.Alpine) {
            try {
              const alpineData = window.Alpine.$data(progressContainer);
              if (alpineData && alpineData.active && alpineData.progressPercent > 0) {
                const state = this.getState(containerId);
                state.progress = alpineData.progressPercent;
                state.active = alpineData.active;
                state.initialized = alpineData.initialized;
                return alpineData.progressPercent;
              }
            } catch(e) {
              console.error('Error saving free shipping progress:', e);
            }
          }
          
          return 0;
        },
        
        // Save progress from all visible free shipping bars before cart updates
        saveAllProgress() {
          const containers = [
            document.getElementById('CartDrawer'),
            document.getElementById('main-cart-freeshipping-bar'),
            document.getElementById('main-cart-freeshipping-bar-mobile'),
            document.getElementById('popup-freeshipping-bar'),
            document.getElementById('main-cart-items')?.querySelector('#main-cart-freeshipping-bar')
          ].filter(Boolean);
          
          containers.forEach(container => this.saveProgress(container));
        },
        
        // Monitor cart changes via browser events
        monitorCartChanges() {
          document.addEventListener('eurus:cart:items-changed', () => {
            this.saveAllProgress();
          });
          
          document.addEventListener('cart:updated', () => {
            this.saveAllProgress();
          });
          
          // Also listen for cart API requests via PerformanceObserver as fallback
          if (window.PerformanceObserver) {
            const cartObserver = new PerformanceObserver((list) => {
              list.getEntries().forEach((entry) => {
                const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
                const isCartChangeRequest = /\/cart\/(add|update|change|clear)\.js/.test(entry.name);
                if (isValidRequestType && isCartChangeRequest) {
                  requestAnimationFrame(() => {
                    this.saveAllProgress();
                  });
                }
              });
            });
            
            try {
              cartObserver.observe({ entryTypes: ['resource'] });
            } catch (e) {
              // PerformanceObserver may not be available in all browsers
            }
          }
        }
      });

      // Alpine data component for free shipping bar
      Alpine.data('freeshippingBar', (initialProgress = 0, containerId = null) => {
        let instanceContainerId = null;
        
        return {
          progressPercent: initialProgress,
          
          // Reference to store state for this instance
          get state() {
            try {
              if (!instanceContainerId || !Alpine.store('freeshippingBar')) return null;
              return Alpine.store('freeshippingBar').getState(instanceContainerId);
            } catch (e) {
              return null;
            }
          },
          
          // Wrapper methods for state, preserves Alpine reactivity
          // active
          get active() {
            return this.state?.active ?? false;
          },
          set active(value) {
            if (this.state) {
              this.state.active = value;
            }
          },
          
          // initialized
          get initialized() {
            return this.state?.initialized ?? false;
          },
          set initialized(value) {
            if (this.state) {
              this.state.initialized = value;
            }
          },
          
          // Returns progressPercent when active, 0 otherwise
          get displayWidth() {
            return this.active ? this.progressPercent : 0;
          },
          
          init() {
            // find container
            let container = null;
            if (containerId) {
              container = document.getElementById(containerId) || this.$el.closest(`#${containerId}`);
            } else {
              container = this.$el.closest('#popup-freeshipping-bar, #CartDrawer, #main-cart-freeshipping-bar, #main-cart-freeshipping-bar-mobile');
            }
            
            if (!container || !container.id) return;
            
            instanceContainerId = container.id;
            const progressFill = this.$el.querySelector('.freeshipping-progress-bar-fill');
            
            if (this.state && this.$store.freeshippingBar) {
              const storedProgress = this.state.progress;
              
              // Restore saved progress if available
              if (storedProgress > 0) {
                const newPercent = initialProgress;
                
                if (progressFill) {
                  this.progressPercent = storedProgress;
                  this.active = true;
                  this.initialized = true;
                  
                  this.$nextTick(() => {
                    // Disable transition temporarily to set width instantly
                    progressFill.style.transition = 'none';
                    progressFill.style.width = storedProgress + '%';
                    
                    void progressFill.offsetHeight;
                    
                    // Re-enable transition and animate to new value
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        progressFill.style.transition = '';
                        this.progressPercent = newPercent;
                      });
                    });
                  });
                } else {
                  // No progressFill element yet, set values directly
                  this.progressPercent = storedProgress;
                  this.active = true;
                  this.initialized = true;
                }
                
                return;
              }
              
              // For CartDrawer or always visible containers, activate immediately
              if (container.id === 'CartDrawer' || container.dataset.alwaysVisible === 'true') {
                this.active = true;
                this.initialized = true;
              }
            }
            
            // Save progress when animation completes (for first update)
            if (progressFill && this.active && this.progressPercent > 0) {
              const saveProgressOnComplete = () => {
                if (this.$store.freeshippingBar && instanceContainerId) {
                  const state = this.$store.freeshippingBar.getState(instanceContainerId);
                  if (state.progress === 0 || state.progress < this.progressPercent) {
                    state.progress = this.progressPercent;
                    state.active = this.active;
                    state.initialized = this.initialized;
                  }
                }
              };
              
              // Listen for transition end to save progress after initial animation
              progressFill.addEventListener('transitionend', saveProgressOnComplete, { once: true });
              
              // Fallback timeout in case transitionend doesn't fire
              setTimeout(() => {
                saveProgressOnComplete();
              }, 600);
            }
          }
        };
      });
      
      Alpine.store('freeshippingBar').monitorCartChanges();
    });
  });
}
